import { useMemo } from 'react'
import { useTheme } from '@mui/material/styles'
import { IonContent, IonPage, useIonToast } from '@ionic/react'
import Typography from '@mui/material/Typography'
import UpgradeIcon from '@mui/icons-material/Upgrade'
import styled from 'styled-components'
import { ActionHeader } from './Header'
import { useToggleTowerForecast } from '../data/hooks/useToggleTowerForecast'
import ProgressBar from './ProgressBar'
import Toast from '../utils/Toast'

// padding:20px 26px 40px 26px;
const MasterDiv = styled.div`
	display: flex;
	justify-content: center;
	flex-direction: column;
	padding: 10px;

	& > p,
	& > div,
	& > label {
		margin-bottom: 15px;
	}

	& .invite-input > div {
		height: 184px;
	}

	& .it-toolbar {
		display: grid;
		grid-template-rows: 60px;
		grid-template-columns: 100px 1fr 100px;
	}

	& .upgrade {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding-top: 6px;
		margin-right:6px;

		& p {
			display: flex;
			justify-content: center;
		}

		& .upgrade-btn {
			cursor: pointer;
			height: 28px;
			width: 96px;
			font-size: 14px;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			background: var(--mu-primary-dark);
			background: -webkit-linear-gradient(to right, var(--mu-primary-dark), var(--mu-primary-light));
			background: linear-gradient(to right, var(--mu-primary-dark), var(--mu-primary-light));
			color: white;
			border-radius: 30px;
			font-weight: 500;

			& .upgrade-text {
				display: flex;
				align-items: center;
				justify-content: center;
				margin-left: -5px;
			}

			& svg {
				font-size:20px;
				margin-right: 3px;
			}
		}

		&.muted {
			& .upgrade-btn {
				background: #e2ebf3;
				color:var(--mu-primary-dark);
				& svg {
					font-size:16px;
				}
			}
		}

		&.get-pro {
			padding-top: 0px;
			& .upgrade-btn {
				width: 132px;
			}
		}
	}
`

const PlanButton = ({ ...props }) => {
	return (<div className={"upgrade" + (props.muted ? ' muted' : '')}>
		<div className="upgrade-btn" onClick={props.upgrade}>
			<div className="upgrade-text">
				{props.icon}
				{props.label}
			</div>
		</div>
	</div>)
}

const ToggleTowerPopup = ({ ...props }:any) => {
	const tower_id = props.tower_id

	const ionToast = useIonToast()
	const toast = useMemo(() => new Toast(...ionToast, { duration:4000 }), [ionToast])

	const {
		toggle_tower,
		can_add_tower,
		get_tower_toggle_info,
		get_self_toggled_count,
		is_saving: loading,
		is_basic,
		is_pro,
		is_org
	} = useToggleTowerForecast({
		onCompleted: () => {
			if (props.on_toggled)
				props.on_toggled()
		},
		on_error: (error: any) => {
			console.error(error)
			toast.show('Failed to toggle tower. Please contact support for further help.', { error: true, closeText: 'close' })
		}
	})

	// Get quota info using the new logic (only counts self-toggled towers)
	const self_toggled_count = get_self_toggled_count()
	const tower_info = get_tower_toggle_info(tower_id)

	const is_basic_or_pro = is_basic || is_pro
	let text = null
	let upgrade_to_org = false
	let main_action = 'Toggle'
	let show_team_message = false

	// Check if this tower is team-toggled (cannot be modified)
	if (tower_info.is_team_toggled) {
		show_team_message = true
		main_action = 'Close'
		text = `This tower's 24-hour forecast was enabled by one of your teams. You cannot disable it, but it does not count against your personal quota of ${is_basic_or_pro ? '10' : 'unlimited'} towers.`
	} else if (is_basic_or_pro) {
		const quota_check = can_add_tower(tower_id)
		upgrade_to_org = true
		if (!quota_check.can && !tower_info.is_toggled) {
			main_action = 'Upgrade'
			text = `You have reached the maximum number of towers for which you can access the 24 hours forecast (10 towers). To toggle the 24 hours forecast for this tower, you can either untoggle other towers or upgrade to the Organisation plan.`
		} else {
			const s = self_toggled_count == 1 ? '' : 's'
			const plan_name = is_basic ? 'Solo' : 'Professional'
			text = `You have used ${self_toggled_count} tower${s} out of your personal quota (10 in ${plan_name} plan). Use the "TOGGLE" button in the top-right corner of this popup to toggle the 24 hours forecast on this tower. If you wish to toggle the 24 hours forecast on more than 10 towers, please upgrade to the Organisation plan.`
		}
	} else if (is_org) {
		text = `Please use the switch button below to toggle the 24 hours forecast on this tower. Your Organisation plan has unlimited towers.`
	} else {
		text = `Please use the switch button below to toggle the 24 hours forecast on this tower.`
	}

	const theme = useTheme()
	const css_vars: any = {
		'--mu-primary-light': theme.palette.primary.light,
		'--mu-primary-dark': theme.palette.primary.dark,
		'--mu-primary-main': theme.palette.primary.main
	}

	const on_cancel = props.onCancel || (() => null)

	const on_toggled = () => {
		// If team-toggled, just close the modal
		if (show_team_message) {
			on_cancel()
			return
		}

		const result = toggle_tower(tower_id, true)
		if (!result.success) {
			if (result.is_team_toggled) {
				toast.show(result.error || 'This tower was enabled by your team and cannot be modified.', { error: false, closeText: 'close' })
			} else {
				toast.show(result.error || 'Failed to toggle tower.', { error: true, closeText: 'close' })
			}
		}
	}

	const upgrade = () => {
		if (props.upgrade)
			props.upgrade()
	}

	return (
		<IonPage {...props}>
			{loading && <ProgressBar />}
			<ActionHeader actionName={main_action} onCancel={on_cancel} onConfirm={main_action == 'Upgrade' ? upgrade : main_action == 'Close' ? on_cancel : on_toggled} />
			<IonContent className="ion-padding">
				<MasterDiv>
					<div className="upgrade" style={css_vars}>
						<Typography sx={{ marginBottom: '40px' }}>
							{text}
						</Typography>{show_team_message ? null : !upgrade_to_org ? null :
							<PlanButton
								upgrade={upgrade}
								icon={<UpgradeIcon />}
								label="Upgrade" />}
					</div>
				</MasterDiv>
			</IonContent>
		</IonPage>)
}

export default ToggleTowerPopup